import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  MapPin,
  Briefcase,
  Globe,
  Upload,
  FileText,
  X,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Reveal, Eyebrow } from '../components/Shared';
import { getJobById } from '../data/careers';
import { L } from '../i18n/LocaleProvider';

export default function CareerDetails() {
  const { id } = useParams();
  const staticJob = getJobById(id);
  const [job, setJob] = useState(staticJob);

  // Fetch live opportunity details from database
  useEffect(() => {
    const backendUrl =
      process.env.REACT_APP_BACKEND_URL ||
      (typeof window !== 'undefined' && window.location.port === '3000'
        ? 'http://localhost:5000'
        : '');

    fetch(`${backendUrl}/api/careers/opportunities/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then((data) => {
        if (data && data.id) {
          setJob(data);
        }
      })
      .catch((err) => {
        console.warn('Fallback to bundled job:', err);
      });
  }, [id]);

  // Form State
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    portfolioUrl: '',
    experienceYears: '3-5 years',
    coverNote: '',
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  // Cover Note file upload state
  const [coverFile, setCoverFile] = useState(null);
  const [coverFileError, setCoverFileError] = useState('');
  const [coverDragOver, setCoverDragOver] = useState(false);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submittedRecord, setSubmittedRecord] = useState(null);
  const fileInputRef = useRef(null);
  const coverFileInputRef = useRef(null);
  const formRef = useRef(null);

  // Update dynamic document title
  useEffect(() => {
    if (job) {
      document.title = `${job.title} — Careers | TechSpitze`;
    }
  }, [job]);

  if (!job) {
    return (
      <L>
        <main id="main-content" className="container career-details-page" data-testid="career-not-found">
          <div className="careers-empty-state glass" style={{ maxWidth: 640, margin: '80px auto' }}>
            <div className="empty-icon-wrap">
              <Sparkles size={28} />
            </div>
            <h2>Opportunity Not Found</h2>
            <p>The role you are looking for may have been filled or the link may have expired.</p>
            <Link to="/careers" className="action-button button-primary">
              <ArrowLeft size={16} /> Explore All Openings
            </Link>
          </div>
        </main>
      </L>
    );
  }

  const readFileAsBase64 = (file) => {
    return new Promise((resolve) => {
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => {
        const resultStr = String(reader.result || '');
        const base64 = resultStr.includes(',') ? resultStr.split(',')[1] : resultStr;
        resolve(base64);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (submitError) setSubmitError('');
  };

  const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

  const validateFile = (file, setErrorFn) => {
    if (!file) return true;
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!validTypes.includes(file.type) && !/\.(pdf|doc|docx)$/i.test(file.name)) {
      setErrorFn('Please upload a PDF, DOC, or DOCX document.');
      return false;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const mbSize = (file.size / (1024 * 1024)).toFixed(1);
      setErrorFn(`File size (${mbSize}MB) exceeds the 5MB limit. Please upload a smaller document.`);
      return false;
    }
    setErrorFn('');
    return true;
  };

  // Resume handlers
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (validateFile(file, setFileError)) {
      setSelectedFile(file);
      setFileError('');
      if (errors.resume) setErrors((prev) => ({ ...prev, resume: undefined }));
    } else {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    if (submitError) setSubmitError('');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (validateFile(file, setFileError)) {
      setSelectedFile(file);
      setFileError('');
      if (errors.resume) setErrors((prev) => ({ ...prev, resume: undefined }));
    } else {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    if (submitError) setSubmitError('');
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setFileError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (submitError) setSubmitError('');
  };

  // Cover Note file handlers
  const handleCoverFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (validateFile(file, setCoverFileError)) {
      setCoverFile(file);
      setCoverFileError('');
    } else {
      setCoverFile(null);
      if (coverFileInputRef.current) coverFileInputRef.current.value = '';
    }
    if (submitError) setSubmitError('');
  };

  const handleCoverDragOver = (e) => {
    e.preventDefault();
    setCoverDragOver(true);
  };

  const handleCoverDragLeave = () => {
    setCoverDragOver(false);
  };

  const handleCoverDrop = (e) => {
    e.preventDefault();
    setCoverDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (validateFile(file, setCoverFileError)) {
      setCoverFile(file);
      setCoverFileError('');
    } else {
      setCoverFile(null);
      if (coverFileInputRef.current) coverFileInputRef.current.value = '';
    }
    if (submitError) setSubmitError('');
  };

  const removeCoverFile = (e) => {
    e.stopPropagation();
    setCoverFile(null);
    setCoverFileError('');
    if (coverFileInputRef.current) coverFileInputRef.current.value = '';
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const nextErrors = {};

    if (!formData.fullName.trim() || formData.fullName.trim().length < 2) {
      nextErrors.fullName = 'Please enter your full name.';
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = 'Please provide a valid email address.';
    }

    if (!formData.location.trim()) {
      nextErrors.location = 'Please share your current city / country.';
    }

    if (!selectedFile) {
      nextErrors.resume = 'Please attach your resume or CV (PDF/DOCX, max 5MB).';
    } else if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      const mb = (selectedFile.size / (1024 * 1024)).toFixed(1);
      nextErrors.resume = `Resume file size (${mb}MB) exceeds the 5MB limit.`;
    } else if (!/\.(pdf|doc|docx)$/i.test(selectedFile.name)) {
      nextErrors.resume = 'Resume must be a PDF, DOC, or DOCX document.';
    }

    if (coverFile) {
      if (coverFile.size > MAX_FILE_SIZE_BYTES) {
        const mb = (coverFile.size / (1024 * 1024)).toFixed(1);
        setCoverFileError(`Cover note file size (${mb}MB) exceeds the 5MB limit.`);
        nextErrors.cover = 'Cover letter file exceeds 5MB limit.';
      } else if (!/\.(pdf|doc|docx)$/i.test(coverFile.name)) {
        setCoverFileError('Cover letter must be a PDF, DOC, or DOCX document.');
        nextErrors.cover = 'Cover letter must be a PDF, DOC, or DOCX document.';
      }
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      const firstErrorKey = Object.keys(nextErrors)[0];
      const targetInput = formRef.current?.querySelector(`[name="${firstErrorKey}"]`);
      targetInput?.focus();
      return;
    }

    setIsSubmitting(true);

    try {
      const base64Resume = selectedFile ? await readFileAsBase64(selectedFile) : null;
      const base64Cover = coverFile ? await readFileAsBase64(coverFile) : null;

      let coverNotePayload = null;
      if (coverFile) {
        coverNotePayload = {
          filename: coverFile.name,
          content_type: coverFile.type || 'application/pdf',
          size_bytes: coverFile.size,
          data_base64: base64Cover,
          text: formData.coverNote.trim() || undefined,
        };
      } else if (formData.coverNote.trim()) {
        coverNotePayload = {
          text: formData.coverNote.trim(),
        };
      }

      const payload = {
        job_id: job.id,
        job_title: job.title,
        full_name: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        location: formData.location.trim(),
        portfolio_url: formData.portfolioUrl.trim() || null,
        experience_years: formData.experienceYears,
        resume: selectedFile
          ? {
              filename: selectedFile.name,
              content_type: selectedFile.type || 'application/pdf',
              size_bytes: selectedFile.size,
              data_base64: base64Resume,
            }
          : null,
        cover_note: coverNotePayload,
      };

      const backendUrl =
        process.env.REACT_APP_BACKEND_URL ||
        (typeof window !== 'undefined' && window.location.port === '3000'
          ? 'http://localhost:5000'
          : '');

      const response = await fetch(`${backendUrl}/api/careers/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to submit application. Please try again.');
      }

      const created = await response.json();
      setSubmittedRecord(created);
      setIsSubmitted(true);
      window.scrollTo({ top: 120, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong while submitting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      location: '',
      portfolioUrl: '',
      experienceYears: '3-5 years',
      coverNote: '',
    });
    setSelectedFile(null);
    setCoverFile(null);
    setErrors({});
    setFileError('');
    setCoverFileError('');
    setSubmitError('');
    setSubmittedRecord(null);
    setIsSubmitted(false);
  };

  return (
    <L>
      <main id="main-content" className="container career-details-page" data-testid="career-details-page">
        {/* Breadcrumb Navigation */}
        <Link to="/careers" className="career-breadcrumb" data-testid="career-details-back">
          <ArrowLeft size={16} aria-hidden="true" />
          <span>Back to All Openings</span>
        </Link>

        {/* Opportunity Header */}
        <header className="career-details-header">
          <div className="details-eyebrow-row">
            <Eyebrow id="career-details-eyebrow" dot>
              {(job.department || 'General').toUpperCase()} · {(job.type || 'Full Time').toUpperCase()}
            </Eyebrow>
          </div>

          <h1 className="details-hero-title" data-testid="career-details-title">
            {job.title}
          </h1>

          <div className="details-meta-bar">
            <span className="details-meta-chip" data-testid="details-meta-location">
              <MapPin size={14} aria-hidden="true" />
              <span>{job.location}</span>
            </span>
            <span className="details-meta-chip" data-testid="details-meta-type">
              <Briefcase size={14} aria-hidden="true" />
              <span>{job.type}</span>
            </span>
            <span className="details-meta-chip" data-testid="details-meta-workplace">
              <Globe size={14} aria-hidden="true" />
              <span>{job.workplaceType}</span>
            </span>
            <span className="details-meta-chip" data-testid="details-meta-exp">
              <span>{job.experience}</span>
            </span>
          </div>
        </header>

        {/* Main 2-Column Responsive Layout */}
        <div className="career-details-layout">
          {/* Left Column: Description & Qualifications */}
          <div className="career-description-column" data-testid="career-details-content">
            {/* Overview */}
            <section className="details-section" aria-label="Role Overview">
              <h2>About the Opportunity</h2>
              <p data-testid="career-overview">{job.overview}</p>
            </section>

            {/* Responsibilities */}
            {job.responsibilities && job.responsibilities.length > 0 && (
              <section className="details-section" aria-label="Responsibilities">
                <h2>{job.sectionHeaders?.responsibilities || "What You’ll Do"}</h2>
                <ul className="details-bullets" data-testid="career-responsibilities">
                  {job.responsibilities.map((resp, i) => (
                    <li key={i}>
                      <CheckCircle2 size={18} className="bullet-icon" aria-hidden="true" />
                      <span>{resp}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Requirements */}
            {job.requirements && job.requirements.length > 0 && (
              <section className="details-section" aria-label="Requirements">
                <h2>{job.sectionHeaders?.requirements || "What You Bring"}</h2>
                <ul className="details-bullets" data-testid="career-requirements">
                  {job.requirements.map((req, i) => (
                    <li key={i}>
                      <CheckCircle2 size={18} className="bullet-icon" aria-hidden="true" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Nice to Have */}
            {job.niceToHave && job.niceToHave.length > 0 && (
              <section className="details-section" aria-label="Nice to have">
                <h2>{job.sectionHeaders?.niceToHave || "Bonus Points"}</h2>
                <ul className="details-bullets" data-testid="career-nice-to-have">
                  {job.niceToHave.map((item, i) => (
                    <li key={i}>
                      <ArrowRight size={18} className="bullet-icon" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Benefits */}
            {job.benefits && job.benefits.length > 0 && (
              <section className="details-section" aria-label="Benefits">
                <h2>{job.sectionHeaders?.benefits || "What We Offer"}</h2>
                <ul className="details-bullets" data-testid="career-benefits">
                  {job.benefits.map((benefit, i) => (
                    <li key={i}>
                      <CheckCircle2 size={18} className="bullet-icon" aria-hidden="true" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Culture Note */}
            <div className="details-quote-box glass">
              “At TechSpitze, our edge comes from curious people who take ownership. You will collaborate with peers across five countries, solve tough technical challenges, and build systems that make an enduring difference.”
            </div>
          </div>

          {/* Right Column: Sticky Application Form beside description */}
          <aside className="career-form-column" aria-label="Application form">
            <div className="application-form-card glass" data-testid="career-apply-card">
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="app-success-card"
                  role="status"
                  aria-live="polite"
                  data-testid="career-apply-success"
                >
                  <div className="success-check-circle">
                    <CheckCircle2 size={36} />
                  </div>
                  <h3>Application Submitted!</h3>
                  <p>
                    Thank you, <strong style={{ color: '#fff' }}>{formData.fullName}</strong>. We have received your application for the{' '}
                    <strong style={{ color: '#7ea6ff' }}>{job.title}</strong> role.
                  </p>

                  <div className="success-detail-box">
                    {submittedRecord?.id && (
                      <div className="success-detail-row">
                        <span className="detail-k">Application Ref:</span>
                        <span className="detail-v" style={{ fontFamily: 'monospace', fontSize: 11.5 }}>
                          {submittedRecord.id.slice(0, 8)}...
                        </span>
                      </div>
                    )}
                    <div className="success-detail-row">
                      <span className="detail-k">Role:</span>
                      <span className="detail-v">{job.title}</span>
                    </div>
                    <div className="success-detail-row">
                      <span className="detail-k">Contact Email:</span>
                      <span className="detail-v">{formData.email}</span>
                    </div>
                    <div className="success-detail-row">
                      <span className="detail-k">Resume Attached:</span>
                      <span className="detail-v">{selectedFile?.name || 'Uploaded'}</span>
                    </div>
                    {(coverFile || formData.coverNote) && (
                      <div className="success-detail-row">
                        <span className="detail-k">Cover Note / Letter:</span>
                        <span className="detail-v">{coverFile?.name || 'Written note included'}</span>
                      </div>
                    )}
                  </div>

                  <p style={{ fontSize: 13, color: '#8fa0bd', marginBottom: 20 }}>
                    Our talent acquisition team will review your profile and reach out within 3 business days.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <Link to="/careers" className="action-button button-primary">
                      <span>Explore Other Roles</span>
                      <ArrowUpRight size={16} />
                    </Link>
                    <Button
                      variant="ghost"
                      onClick={resetForm}
                      className="action-button button-secondary"
                      data-testid="career-reset-btn"
                    >
                      <span>Submit another application</span>
                      <RotateCcw size={15} />
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} noValidate data-testid="career-apply-form">
                  <div className="app-form-header">
                    <h3>
                      <span>Apply for this role</span>
                      <ArrowUpRight size={20} aria-hidden="true" />
                    </h3>
                    <p>Submit your details to join our {job.department} team.</p>
                  </div>

                  <div className="app-form-grid">
                    {/* Full Name */}
                    <div className="app-field">
                      <label htmlFor="app-name">
                        Full Name <span className="required-star">*</span>
                      </label>
                      <input
                        id="app-name"
                        name="fullName"
                        type="text"
                        placeholder="Alex Morgan"
                        autoComplete="name"
                        required
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className={`app-input ${errors.fullName ? 'input-error' : ''}`}
                        data-testid="career-apply-name"
                        aria-invalid={!!errors.fullName}
                        aria-describedby={errors.fullName ? 'name-error' : undefined}
                      />
                      {errors.fullName && (
                        <p id="name-error" className="app-field-error">
                          {errors.fullName}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="app-field">
                      <label htmlFor="app-email">
                        Email Address <span className="required-star">*</span>
                      </label>
                      <input
                        id="app-email"
                        name="email"
                        type="email"
                        placeholder="alex@company.com"
                        autoComplete="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`app-input ${errors.email ? 'input-error' : ''}`}
                        data-testid="career-apply-email"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                      />
                      {errors.email && (
                        <p id="email-error" className="app-field-error">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    {/* Phone & Location */}
                    <div className="app-field-row">
                      <div className="app-field">
                        <label htmlFor="app-phone">
                          Phone Number <small>(optional)</small>
                        </label>
                        <input
                          id="app-phone"
                          name="phone"
                          type="tel"
                          placeholder="+1 (555) 000-0000"
                          autoComplete="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="app-input"
                          data-testid="career-apply-phone"
                        />
                      </div>

                      <div className="app-field">
                        <label htmlFor="app-location">
                          Current Location <span className="required-star">*</span>
                        </label>
                        <input
                          id="app-location"
                          name="location"
                          type="text"
                          placeholder="e.g. Toronto, Canada"
                          required
                          value={formData.location}
                          onChange={handleInputChange}
                          className={`app-input ${errors.location ? 'input-error' : ''}`}
                          data-testid="career-apply-location"
                          aria-invalid={!!errors.location}
                          aria-describedby={errors.location ? 'loc-error' : undefined}
                        />
                        {errors.location && (
                          <p id="loc-error" className="app-field-error">
                            {errors.location}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Portfolio / LinkedIn & Experience */}
                    <div className="app-field-row">
                      <div className="app-field">
                        <label htmlFor="app-portfolio">
                          LinkedIn / Portfolio <small>(optional)</small>
                        </label>
                        <input
                          id="app-portfolio"
                          name="portfolioUrl"
                          type="url"
                          placeholder="https://linkedin.com/in/..."
                          value={formData.portfolioUrl}
                          onChange={handleInputChange}
                          className="app-input"
                          data-testid="career-apply-portfolio"
                        />
                      </div>

                      <div className="app-field">
                        <label htmlFor="app-experience">Relevant Experience</label>
                        <select
                          id="app-experience"
                          name="experienceYears"
                          value={formData.experienceYears}
                          onChange={handleInputChange}
                          className="app-input"
                          data-testid="career-apply-experience"
                        >
                          <option value="Fresher / 0-1 year">Fresher / 0-1 year</option>
                          <option value="1-2 years">1-2 years</option>
                          <option value="3-5 years">3-5 years</option>
                          <option value="6-8 years">6-8 years</option>
                          <option value="9+ years">9+ years</option>
                        </select>
                      </div>
                    </div>

                    {/* Resume Upload */}
                    <div className="app-field">
                      <label>
                        Resume / CV <span className="required-star">*</span>
                      </label>
                      <div
                        className={`file-upload-box ${dragOver ? 'drag-over' : ''} ${selectedFile ? 'has-file' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="career-apply-dropzone"
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          name="resume"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          className="file-input-hidden"
                          aria-label="Upload your resume"
                        />

                        {selectedFile ? (
                          <div className="selected-file-info">
                            <FileText size={20} className="file-upload-icon" />
                            <div style={{ textAlign: 'left', overflow: 'hidden' }}>
                              <div style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', fontWeight: 600 }}>
                                {selectedFile.name}
                              </div>
                              <small style={{ color: '#8898b0' }}>
                                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                              </small>
                            </div>
                            <button
                              type="button"
                              onClick={removeFile}
                              className="file-remove-btn"
                              aria-label="Remove uploaded file"
                              data-testid="career-file-remove"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <Upload size={24} className="file-upload-icon" />
                            <span className="file-upload-label">Click to browse or drop file here</span>
                            <span className="file-upload-sub">PDF, DOC, or DOCX (Max 5MB)</span>
                          </>
                        )}
                      </div>
                      {(errors.resume || fileError) && (
                        <p className="app-field-error" data-testid="career-resume-error">
                          {errors.resume || fileError}
                        </p>
                      )}
                    </div>

                    {/* Cover Note / File Upload */}
                    <div className="app-field">
                      <label>
                        Cover Note / Letter <small>(file upload optional)</small>
                      </label>
                      <div
                        className={`file-upload-box ${coverDragOver ? 'drag-over' : ''} ${coverFile ? 'has-file' : ''}`}
                        onDragOver={handleCoverDragOver}
                        onDragLeave={handleCoverDragLeave}
                        onDrop={handleCoverDrop}
                        onClick={() => coverFileInputRef.current?.click()}
                        data-testid="career-cover-dropzone"
                      >
                        <input
                          ref={coverFileInputRef}
                          type="file"
                          name="coverLetter"
                          accept=".pdf,.doc,.docx"
                          onChange={handleCoverFileChange}
                          className="file-input-hidden"
                          aria-label="Upload your cover note or letter"
                        />

                        {coverFile ? (
                          <div className="selected-file-info">
                            <FileText size={20} className="file-upload-icon" />
                            <div style={{ textAlign: 'left', overflow: 'hidden' }}>
                              <div style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', fontWeight: 600 }}>
                                {coverFile.name}
                              </div>
                              <small style={{ color: '#8898b0' }}>
                                {(coverFile.size / (1024 * 1024)).toFixed(2)} MB
                              </small>
                            </div>
                            <button
                              type="button"
                              onClick={removeCoverFile}
                              className="file-remove-btn"
                              aria-label="Remove uploaded cover letter"
                              data-testid="career-cover-file-remove"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <Upload size={24} className="file-upload-icon" />
                            <span className="file-upload-label">Upload cover letter or note document</span>
                            <span className="file-upload-sub">PDF, DOC, or DOCX (Max 5MB)</span>
                          </>
                        )}
                      </div>
                      {coverFileError && (
                        <p className="app-field-error" data-testid="career-cover-error">
                          {coverFileError}
                        </p>
                      )}
                    </div>

                    {/* Additional Message / Note */}
                    <div className="app-field">
                      <label htmlFor="app-note">
                        Additional Note / Comments <small>(optional)</small>
                      </label>
                      <textarea
                        id="app-note"
                        name="coverNote"
                        rows={3}
                        placeholder="Add any extra note or context for the hiring team…"
                        value={formData.coverNote}
                        onChange={handleInputChange}
                        maxLength={2000}
                        className="app-input app-textarea"
                        data-testid="career-apply-note"
                      />
                    </div>
                  </div>

                  {/* Submit Row */}
                  <div className="app-submit-row">
                    {submitError && (
                      <p
                        className="app-field-error"
                        style={{ textAlign: 'center', fontSize: 13 }}
                        data-testid="career-submit-error"
                      >
                        {submitError}
                      </p>
                    )}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="action-button button-primary app-submit-btn"
                      data-testid="career-apply-submit"
                    >
                      {isSubmitting ? (
                        <span>Submitting Application…</span>
                      ) : (
                        <>
                          <span>Submit Application</span>
                          <ArrowRight size={17} />
                        </>
                      )}
                    </Button>
                    <p className="app-privacy-note">
                      Frontend preview ready. Your data will be transmitted securely to our recruitment pipeline once connected.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </aside>
        </div>
      </main>
    </L>
  );
}
